import { useEffect } from 'react';

type SEOProps = {
  /** Page title. " — MTC" is appended automatically if not already present. */
  title: string;
  description?: string | undefined;
  /** Absolute URL of the OG/Twitter image. */
  ogImage?: string | null | undefined;
  /** Schema.org JSON-LD object — written into a <script type="application/ld+json"> tag. */
  schemaOrg?: object | undefined;
};

function ensureMeta(name: string, content: string | null | undefined, attr: 'name' | 'property' = 'name') {
  const selector = `meta[${attr}="${name}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!content) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/** Canonical origin — the apex is canonical (www serves the same content). */
const SITE_ORIGIN = 'https://moroccantrainingcenter.com';

function ensureCanonical() {
  const href = SITE_ORIGIN + window.location.pathname;
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  // og:url mirrors the canonical so social shares point at the apex too.
  ensureMeta('og:url', href, 'property');
}

const SCHEMA_SCRIPT_ID = 'mtc-schema-org';

function ensureSchemaOrg(obj: object | undefined) {
  let el = document.getElementById(SCHEMA_SCRIPT_ID) as HTMLScriptElement | null;
  if (!obj) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.id = SCHEMA_SCRIPT_ID;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}

/**
 * Imperative SEO management for the SPA: sets <title> + meta tags + JSON-LD
 * per-route. No SSR — Googlebot executes JS, but heavier sharers (Twitter,
 * LinkedIn preview crawlers) only see what's in index.html. The defaults
 * baked into index.html cover that case.
 */
export function useSEO({ title, description, ogImage, schemaOrg }: SEOProps) {
  useEffect(() => {
    const fullTitle = title.includes('MTC') ? title : `${title} — MTC`;
    document.title = fullTitle;

    ensureMeta('description', description);
    ensureMeta('og:title', fullTitle, 'property');
    ensureMeta('og:description', description, 'property');
    ensureMeta('twitter:title', fullTitle);
    ensureMeta('twitter:description', description);
    if (ogImage) {
      ensureMeta('og:image', ogImage, 'property');
      ensureMeta('twitter:image', ogImage);
    }
    ensureCanonical();
    ensureSchemaOrg(schemaOrg);
  }, [title, description, ogImage, schemaOrg]);
}
